import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CircleHelp, Loader2, MoreVertical, X } from 'lucide-react';
import { TbChartRadar } from 'react-icons/tb';

import { MathTextBlock } from '../components/MathTextBlock';
import { MembershipPressureRadarChart, MembershipPressureRadarLegend } from '../components/ttq/MembershipPressureRadarChart';
import { SectionCard } from '../components/ttq/SectionCard';
import { useActiveSubject } from '../context/ActiveSubjectContext';
import { useAuth } from '../context/AuthContext';
import {
  ttqMembershipPressureService,
  type TtqMembershipPressureRecommendation,
} from '../services/ttqMembershipPressureService';
import { isTtqSubjectEnabled, resolveTtqDockSubject } from '../services/ttqService';
import { invalidateTtqSessionList } from '../services/ttqSessionListCache';
import { SUBJECT_CONFIG } from '../utils/subjectConfig';
import {
  buildRecommendationDebugPayload,
  formatParticleMembershipLabel,
  formatPressureLabelList,
  formatPressureScoreLine,
  formatPressureWeightLine,
  formatSpineLabel,
  MEMBERSHIP_PRESSURE_SCORING_HELP,
  peerScoreSeriesFor,
  recommendationApproveButtonCopy,
  recommendationFusionOutcomeLabel,
  recommendationNearestRingLabel,
  recommendationPeerMembershipSummary,
  recommendationStemText,
} from '../utils/ttqMembershipPressureDisplay';

export function TtqMembershipPressureDetailPage() {
  const { recommendationId = '' } = useParams<{ recommendationId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeSubject: subject } = useActiveSubject();
  const ttqSubject = isTtqSubjectEnabled(subject) ? resolveTtqDockSubject(subject) : null;
  const subjectLabel = SUBJECT_CONFIG[subject]?.label ?? subject;

  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rec, setRec] = useState<TtqMembershipPressureRecommendation | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [debugVisible, setDebugVisible] = useState(false);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [scoreHelpOpen, setScoreHelpOpen] = useState(false);
  const [radarOpen, setRadarOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await ttqMembershipPressureService.listRecommendations('recommended');
      const found = rows.find((row) => row.id === recommendationId) || null;
      if (!found) {
        const all = await ttqMembershipPressureService.listRecommendations('all');
        const again = all.find((row) => row.id === recommendationId) || null;
        setRec(again);
        if (!again) {
          setError('推荐不存在或已处理');
        } else if (again.status !== 'recommended') {
          setError('该推荐已处理，仅可查看');
        }
        return;
      }
      setRec(found);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : '加载推荐详情失败');
    } finally {
      setLoading(false);
    }
  }, [recommendationId]);

  useEffect(() => {
    void load();
  }, [load]);

  const copyText = useCallback(async (key: string, value: string) => {
    const text = String(value || '').trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
    } catch {
      setCopiedKey(null);
    }
  }, []);

  const handleApprove = async () => {
    if (!rec || rec.status !== 'recommended' || approving) return;
    setApproving(true);
    setError(null);
    try {
      const result = await ttqMembershipPressureService.approve(rec.id);
      const userId = String(user?.id || '').trim();
      if (userId && ttqSubject) invalidateTtqSessionList(userId, ttqSubject);
      const sessionId = String(result?.session?.id || rec.targetTtqaId || '').trim();
      if (sessionId) {
        navigate(`/ttq/${sessionId}`);
      } else {
        navigate('/ttq/recommendations');
      }
    } catch (approveError) {
      setError(approveError instanceof Error ? approveError.message : '构造品题失败');
    } finally {
      setApproving(false);
    }
  };

  const isJoin = rec?.action === 'join_existing_ttqa';
  const targetTtqaId = String(rec?.targetTtqaId || '').trim();
  const targetTitle = String(rec?.targetTtqaTitle || '').trim()
    || (targetTtqaId ? `品题 ${targetTtqaId.slice(0, 8)}` : '');
  const memberCount = rec?.targetTtqaMemberCount ?? null;
  const memberPreviews = rec?.targetTtqaMemberPreviews || [];

  const actionLabel = isJoin
    ? `建议并入已有品题${targetTitle ? `「${targetTitle}」` : ''}`
    : '建议新开品题（融合下列同伴）';

  const peerEntries = (rec?.suggestedPeerIds || []).map((peerId, index) => ({
    peerId,
    text: recommendationStemText(
      rec?.suggestedPeerMainQuestions?.[index],
      rec?.suggestedPeerTitles?.[index],
      peerId,
    ),
    membership: rec?.suggestedPeerMemberships?.[index] || null,
    spineLabel: rec?.suggestedPeerSpineLabels?.[index] ?? null,
  }));
  const duplicateStemIds = new Set(
    peerEntries
      .filter((peer, _, all) => {
        const stem = peer.text.replace(/\s+/g, ' ').trim();
        return all.filter((other) => other.text.replace(/\s+/g, ' ').trim() === stem).length > 1;
      })
      .map((peer) => peer.peerId),
  );

  const peerSummary = rec ? recommendationPeerMembershipSummary(rec) : null;
  const fusionOutcome = rec ? recommendationFusionOutcomeLabel(rec) : '';
  const approveCopy = rec
    ? recommendationApproveButtonCopy(rec, approving)
    : { title: '', subtitle: '' };
  const sourceMembershipLabel = rec
    ? formatParticleMembershipLabel(rec.sourceParticleMembership)
    : '';

  const debugRecId = String(rec?.id || recommendationId || '').trim();

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <Link
            to="/ttq/recommendations"
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-surface text-ink hover:bg-black/[0.04]"
            aria-label="返回入围推荐"
          >
            <ArrowLeft size={16} />
          </Link>
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold text-ink">{subjectLabel}推荐详情</h1>
            <p className="mt-1 text-sm text-muted">
              先看源题与同伴是否同质，再决定是否构造品题。
            </p>
          </div>
        </div>
        <div className="relative shrink-0">
          <button
            type="button"
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-surface text-ink hover:bg-black/[0.04]"
            aria-label="更多"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <MoreVertical size={16} />
          </button>
          {menuOpen ? (
            <>
              <button
                type="button"
                className="fixed inset-0 z-30 cursor-default bg-transparent"
                aria-label="关闭菜单"
                onClick={() => setMenuOpen(false)}
              />
              <div className="absolute right-0 z-40 mt-1 min-w-[140px] overflow-hidden rounded-lg border border-border bg-surface py-1 shadow-lg">
                <button
                  type="button"
                  className="block w-full px-3 py-2 text-left text-sm text-ink hover:bg-black/[0.04]"
                  onClick={() => {
                    setMenuOpen(false);
                    setCopiedKey(null);
                    setDebugVisible(true);
                  }}
                >
                  查看详情
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="animate-spin text-primary" size={28} />
        </div>
      ) : null}

      {error ? <p className="text-sm text-danger">{error}</p> : null}

      {!loading && rec ? (
        <>
          <SectionCard tone="accent">
            <p className="text-xs font-semibold text-muted">源颗粒</p>
            <MathTextBlock
              text={recommendationStemText(rec.sourceMainQuestion, rec.sourceParticleTitle, rec.sourceParticleId)}
              variant="document"
              className="mt-1 text-base font-semibold leading-relaxed text-ink"
            />
            <p className={`mt-2 inline-flex rounded-md px-2 py-0.5 text-[11px] font-semibold ${
              rec.sourceParticleMembership?.inTtqa
                ? 'bg-amber-100 text-amber-800'
                : 'bg-black/[0.04] text-muted'
            }`}
            >
              {sourceMembershipLabel}
            </p>
            <p className="mt-1.5 text-[11px] leading-4 text-muted">
              学科主干：{formatSpineLabel(rec.sourceSpineLabel)}
            </p>
            <p className="mt-1 text-[11px] leading-4 text-muted">
              源题考查点：{formatPressureLabelList(rec.sourceExamPointLabels)}
            </p>
            <p className="mt-1 text-[11px] leading-4 text-muted">
              源题隐性能力：{formatPressureLabelList(rec.sourceAbilityKeys)}
            </p>
            <p className="mt-2 text-xs text-muted">
              {actionLabel}
              {' · '}
              {recommendationNearestRingLabel(rec.ringEvidence?.ring)}
              {' · '}
              同伴平均综合分
              {' '}
              {(rec.scores?.total ?? 0).toFixed(2)}
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <p className="text-[11px] tabular-nums text-muted">
                {formatPressureScoreLine(rec.scores)}
                {'（同伴平均）'}
              </p>
              <div className="relative">
                <button
                  type="button"
                  className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-primary"
                  aria-expanded={scoreHelpOpen}
                  onClick={() => setScoreHelpOpen((open) => !open)}
                >
                  怎么打分？
                  <CircleHelp size={12} />
                </button>
                {scoreHelpOpen ? (
                  <>
                    <button
                      type="button"
                      className="fixed inset-0 z-40 cursor-default bg-transparent"
                      aria-label="关闭说明"
                      onClick={() => setScoreHelpOpen(false)}
                    />
                    <div className="absolute left-0 top-full z-50 mt-2 w-[min(24rem,calc(100vw-4rem))] rounded-xl border border-border bg-surface p-4 shadow-lg">
                      <p className="text-sm font-semibold text-ink">入围怎么打分</p>
                      <ul className="mt-2 space-y-2 text-xs leading-5 text-ink">
                        {MEMBERSHIP_PRESSURE_SCORING_HELP.map((line) => (
                          <li key={line}>{line}</li>
                        ))}
                      </ul>
                    </div>
                  </>
                ) : null}
              </div>
              <button
                type="button"
                className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-primary/30 text-primary hover:bg-primary/10"
                aria-label="打开分项雷达图"
                title="分项雷达图"
                onClick={() => setRadarOpen(true)}
              >
                <TbChartRadar size={16} />
              </button>
            </div>
            <p className="mt-2 text-sm font-medium text-ink">{fusionOutcome}</p>
            <p className="mt-1 text-[11px] text-muted">
              权重：{formatPressureWeightLine(rec.scoreWeights)}
            </p>
          </SectionCard>

          {isJoin ? (
            <SectionCard tone="accent">
              <p className="text-xs font-semibold text-muted">目标品题</p>
              {targetTtqaId ? (
                <>
                  <p className="mt-1 text-base font-semibold text-ink">{targetTitle || '未命名品题'}</p>
                  {typeof memberCount === 'number' ? (
                    <p className="mt-1 text-xs text-muted">当前成员 {memberCount} 题</p>
                  ) : null}
                  {memberPreviews.length > 0 ? (
                    <div className="mt-3">
                      <p className="mb-1 text-xs text-muted">品题内已有题目（预览）</p>
                      <ul>
                        {memberPreviews.map((member, index) => (
                          <li
                            key={member.particleId}
                            className={`flex items-start gap-2 py-2 ${index > 0 ? 'border-t border-border/60' : ''}`}
                          >
                            <span className="w-5 shrink-0 text-xs font-bold text-muted">{index + 1}</span>
                            <MathTextBlock
                              text={recommendationStemText(member.mainQuestion, member.title, member.particleId)}
                              variant="document"
                              className="min-w-0 flex-1 text-sm leading-relaxed text-ink"
                            />
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <p className="mt-2 text-sm text-muted">暂无成员题干预览。</p>
                  )}
                  <Link
                    to={`/ttq/${targetTtqaId}`}
                    className="mt-3 inline-block text-sm font-semibold text-primary underline"
                  >
                    先打开该品题查看
                  </Link>
                </>
              ) : (
                <p className="mt-1 text-sm text-muted">推荐缺少目标品题信息。</p>
              )}
            </SectionCard>
          ) : null}

          <SectionCard tone="muted">
            <p className="text-xs font-semibold text-muted">
              {isJoin ? '额外同伴（通常为空）' : '融合同伴'}
            </p>
            {peerSummary ? (
              <p className="mt-1 text-xs text-muted">{peerSummary}</p>
            ) : null}
            {peerEntries.length === 0 ? (
              <p className="mt-2 text-sm text-muted">
                {isJoin
                  ? '并入已有品题：本次只把源颗粒加入上方目标品题。'
                  : '暂无同伴建议。'}
              </p>
            ) : (
              <ul className="mt-1">
                {peerEntries.map((peer, index) => {
                  const inTtqa = Boolean(peer.membership?.inTtqa);
                  const series = peerScoreSeriesFor(rec, peer.peerId);
                  return (
                    <li
                      key={peer.peerId}
                      className={`flex items-start gap-2 py-2 ${index > 0 ? 'border-t border-border/60' : ''}`}
                    >
                      <span className="w-5 shrink-0 text-xs font-bold text-muted">{index + 1}</span>
                      <div className="min-w-0 flex-1">
                        <p className={`mb-1 inline-flex rounded-md px-2 py-0.5 text-[11px] font-semibold ${
                          inTtqa
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-black/[0.04] text-muted'
                        }`}
                        >
                          {formatParticleMembershipLabel(peer.membership)}
                        </p>
                        <p className="mb-1 text-[11px] leading-4 text-muted">
                          学科主干：{formatSpineLabel(peer.spineLabel)}
                          {duplicateStemIds.has(peer.peerId)
                            ? ` · 颗粒 ${peer.peerId.slice(0, 8)}`
                            : ''}
                        </p>
                        {series ? (
                          <>
                            <p className="mb-1 text-[11px] tabular-nums leading-4 text-muted">
                              {series.ring == null ? '环 —' : `Ring${series.ring}`}
                              {' · '}
                              {formatPressureScoreLine(series.scores)}
                            </p>
                            <p className="mb-1 text-[11px] leading-4 text-muted">
                              共享考查点：{formatPressureLabelList(series.sharedExamPointLabels)}
                            </p>
                            <p className="mb-1 text-[11px] leading-4 text-muted">
                              本题考查点：{formatPressureLabelList(series.examPointLabels)}
                            </p>
                            <p className="mb-1 text-[11px] leading-4 text-muted">
                              共享能力：{formatPressureLabelList(series.sharedAbilityKeys)}
                            </p>
                          </>
                        ) : null}
                        <MathTextBlock
                          text={peer.text}
                          variant="document"
                          className="text-sm leading-relaxed text-ink"
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </SectionCard>

          {rec.status === 'recommended' ? (
            <button
              type="button"
              disabled={approving}
              onClick={() => { void handleApprove(); }}
              className="w-full rounded-xl bg-primary px-4 py-3 text-white disabled:opacity-60"
            >
              <span className="block text-base font-semibold">{approveCopy.title}</span>
              {approveCopy.subtitle ? (
                <span className="mt-0.5 block text-sm font-medium text-white/90">
                  {approveCopy.subtitle}
                </span>
              ) : null}
            </button>
          ) : null}
        </>
      ) : null}

      {radarOpen && rec ? (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center">
          <button
            type="button"
            className="absolute inset-0"
            aria-label="关闭"
            onClick={() => setRadarOpen(false)}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="membership-pressure-radar-title"
            className="relative z-10 flex max-h-[min(90vh,720px)] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-xl"
          >
            <div className="flex items-start justify-between gap-3 border-b border-border/70 px-4 py-3">
              <div className="min-w-0">
                <p id="membership-pressure-radar-title" className="text-base font-semibold text-ink">
                  分项雷达图
                </p>
                <p className="mt-1 text-xs leading-5 text-muted">
                  外圈表示与源题该维完全一致。图形越靠外越像源题；彼此越重合，同伴之间越同质。橙虚线与轴上橙点是过闸阈值。
                </p>
              </div>
              <button
                type="button"
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border text-muted hover:bg-black/[0.04]"
                aria-label="关闭"
                onClick={() => setRadarOpen(false)}
              >
                <X size={14} />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 text-ink">
              <MembershipPressureRadarChart rec={rec} />
              <div className="mt-3">
                <MembershipPressureRadarLegend rec={rec} />
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {debugVisible ? (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center">
          <button
            type="button"
            className="absolute inset-0"
            aria-label="关闭"
            onClick={() => setDebugVisible(false)}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="membership-pressure-debug-title"
            className="relative z-10 w-full max-w-md rounded-2xl border border-border bg-surface p-5 shadow-xl"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p id="membership-pressure-debug-title" className="text-base font-semibold text-ink">
                  推荐调试信息
                </p>
                <p className="mt-1 text-xs text-muted">复制推荐 ID 后发给我，便于核对偏差。</p>
              </div>
              <button
                type="button"
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted hover:bg-black/[0.04]"
                aria-label="关闭"
                onClick={() => setDebugVisible(false)}
              >
                <X size={14} />
              </button>
            </div>

            <p className="mt-4 text-xs font-semibold text-muted">推荐 ID</p>
            <p className="mt-1 break-all font-mono text-sm text-ink">{debugRecId || '—'}</p>
            <button
              type="button"
              className="mt-2 w-full rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white"
              onClick={() => { void copyText('recommendationId', debugRecId); }}
            >
              {copiedKey === 'recommendationId' ? '已复制推荐 ID' : '复制推荐 ID'}
            </button>

            {rec ? (
              <>
                <p className="mt-4 text-xs font-semibold text-muted">关联 ID</p>
                <p className="mt-1 whitespace-pre-wrap break-all font-mono text-xs leading-5 text-ink">
                  {`源颗粒：${rec.sourceParticleId}\n目标品题：${rec.targetTtqaId || '（新开）'}\n同伴：${(rec.suggestedPeerIds || []).join(', ') || '无'}`}
                </p>
                <button
                  type="button"
                  className="mt-2 w-full rounded-lg border border-border px-3 py-2 text-sm font-medium text-ink"
                  onClick={() => { void copyText('all', buildRecommendationDebugPayload(rec)); }}
                >
                  {copiedKey === 'all' ? '已复制全部调试信息' : '复制全部调试信息'}
                </button>
              </>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
